import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import {
  getUserSubscription,
  checkSubscriptionAccess,
  getPlanInfo,
} from "../services/subscriptionService";

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      loadSubscription();
    } else if (!isAuthenticated) {
      setSubscription(null);
      setError(null);
    }
  }, [isAuthenticated, user, authLoading]);

  // Refresh on tab visibility (e.g. after Stripe checkout redirect)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && user) {
        loadSubscription();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isAuthenticated, user]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserSubscription();
      setSubscription(data);
    } catch (err) {
      console.error("Failed to load subscription:", err);
      setError(err.message);
      setSubscription({
        status: "free",
        dailyLimit: 0,
        usedToday: 0,
        remaining: 0,
        hasAccess: false,
        reason: "subscription_required",
        org: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscription = () => {
    if (isAuthenticated && user) {
      loadSubscription();
    }
  };

  // Org-aware computed values
  const org = subscription?.org || null;
  const orgPlanType = org?.planType || "free";
  const planInfo = getPlanInfo(orgPlanType);
  const hasAccess = checkSubscriptionAccess(subscription?.status);
  const isUnlimited = !!subscription?.unlimited;

  const isFreePlan = orgPlanType === "free";
  const isPaidPlan = ["starter", "growth", "scale", "individual", "team", "enterprise"].includes(orgPlanType);
  const isTrialPlan = orgPlanType === "trial";
  const isTeamPlan = org?.allowsMembers || ["growth", "scale", "team", "enterprise"].includes(orgPlanType);

  // Credit balance comes from org
  const creditBalance = org?.creditBalance ?? 0;
  const monthlyAllowance = org?.monthlyAllowance ?? 0;
  const monthlySearchLimit = org?.monthlySearchLimit ?? 25;

  const hasNoCredits = isUnlimited ? false : creditBalance <= 0;
  const needsUpgrade = !isPaidPlan && !isTrialPlan && hasNoCredits;

  // Can the user perform a search? (paid = yes, free = rate-limited by backend)
  const canSearch = () => {
    if (isUnlimited || isPaidPlan || isTrialPlan) return true;
    return true; // let backend enforce limit, frontend shows result or error
  };

  // Can the user reveal a contact? (needs credits)
  const canReveal = () => {
    if (isUnlimited) return true;
    return creditBalance > 0;
  };

  // Should we show upgrade prompts?
  const shouldShowUpgrade = () => {
    if (loading || isUnlimited) return false;
    if (isFreePlan && hasNoCredits) return true;
    if (isPaidPlan && hasNoCredits) return false; // show "buy more credits" instead
    return false;
  };

  // Should we show "buy more credits" prompt?
  const shouldShowBuyCredits = () => {
    if (loading || isUnlimited) return false;
    if (isPaidPlan && creditBalance <= 5) return true;
    return false;
  };

  const value = {
    subscription,
    org,
    planInfo,
    hasAccess,
    isFreePlan,
    isPaidPlan,
    isTrialPlan,
    isTeamPlan,
    isUnlimited,
    hasNoCredits,
    needsUpgrade,
    loading,
    error,
    refreshSubscription,

    // Org-level data
    creditBalance,
    monthlyAllowance,
    monthlySearchLimit,
    orgPlanType,
    userRole: org?.userRole || "owner",
    memberCount: org?.memberCount || 1,
    maxSeats: org?.maxSeats || 1,
    allowsMembers: org?.allowsMembers || false,

    // Action helpers
    canSearch,
    canReveal,
    shouldShowUpgrade,
    shouldShowBuyCredits,
    getRemainingCredits: () => creditBalance,

    // Legacy compat
    getUsedCredits: () => subscription?.usedToday || 0,
    getDailyLimit: () => subscription?.dailyLimit || 0,
    canUseFeatures: () => hasAccess && canReveal(),
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
